import { ReactNode, useEffect, useRef, useState } from "react";
import ItemDisplayToolbar, { ItemDisplayTooltipPosition } from "./ItemDisplayToolbar";
import { useSelector } from "react-redux";
import { RootState, store } from "../../store";
import { ItemDisplayState, setDisplayState } from "./itemDisplaySlice";
import { useScrollY } from "../../hooks/useScrollY";
import { Spinner } from "../Spinner/Spinner";
import { FilterState } from "../../functions/filter/FilterComponent";
import { calculateNumberOfFiltersApplied } from "./functions";
import { ItemDisplay } from "./ItemDisplay";
import ItemFilterModal from "./ItemFilterModal";
import { ItemDisplayModalBody } from "./types";
import { useLocation } from "react-router-dom";
import { QueryParamParserBase } from "../../functions/queryparams/QueryParamParser";
import { UserSettings } from "../../models/UserSettings";
import { UserState } from "../../state/userSlice";

export type ItemDisplayPageProps<TItem, TFilterState extends FilterState, TRefreshProps, TFilterKey extends string> = {
    toolbarPosition?: ItemDisplayTooltipPosition,
    enableScrollRefresh?: boolean,
    displayStateId: string,
    items: TItem[],
    modalBody: ItemDisplayModalBody<TFilterState>,
    modalBodyOptionalProps?: any,
    hideSearch?: boolean,
    isLoading?: boolean,
    getDefaultFilterState: (userSettings: UserSettings) => TFilterState,
    refreshTrigger?: TRefreshProps,
    filter: (items: TItem[], filters: TFilterState) => TItem[],
    onFilter?: (filteredItems: TItem[]) => void,
    queryParamParser?: QueryParamParserBase<TFilterKey, TFilterState>,
    itemComponentGenerator: (item: TItem, index: number) => ReactNode,
    hideFilterButton?: boolean,
    hideResetButton?: boolean,
    classNames?: {
        itemsContainer?: string,
        toolbar?: {
            container?: string
        }
    },
    batchSize?: number
}

export default function ItemDisplayPage<TItem, TFilterState extends FilterState, TFilterFunctionProps, TFilterKey extends string>(props: ItemDisplayPageProps<TItem, TFilterState, TFilterFunctionProps, TFilterKey>) {
    const batchSize = props.batchSize ?? 48;
    const contentStartRef = useRef<HTMLDivElement>(null);
    const [items, setItems] = useState<TItem[]>([]);
    const { userSettings } = useSelector<RootState, UserState>(x => x.user);

    const displayState = useSelector<RootState, ItemDisplayState<TFilterState>>(x => x.itemDisplay[props.displayStateId]) ?? {
        id: props.displayStateId,
        scrollY: 0,
        itemsDisplayed: batchSize,
        filters: props.getDefaultFilterState(userSettings),
        isFilterModalOpen: false
    };

    const needsDisplayStateRestore = useRef<boolean>(false);
    const [numberOfFiltersApplied, setNumberOfFiltersApplied] = useState(0);
    const location = useLocation();

    const scrollY = useScrollY({
        delay: 100
    });

    const filter = (filterState: TFilterState) => {
        if (props.queryParamParser) {
            const queryParams = props.queryParamParser.getParams(filterState, props.getDefaultFilterState(userSettings));
            window.history.replaceState({}, "", `${location.pathname}${queryParams}`);
        }

        const stateCopy: ItemDisplayState<TFilterState> = JSON.parse(JSON.stringify(displayState));

        stateCopy.itemsDisplayed = batchSize;
        stateCopy.filters = filterState;
        stateCopy.isFilterModalOpen = false;

        const matchingItems = props.filter(props.items, stateCopy.filters);

        if (props.onFilter) {
            props.onFilter(matchingItems);
        }

        setItems(matchingItems);
        store.dispatch(setDisplayState(stateCopy));
        setNumberOfFiltersApplied(calculateNumberOfFiltersApplied(stateCopy.filters, props.getDefaultFilterState(userSettings)));
        window.scrollTo({ top: (contentStartRef.current?.offsetTop ?? 0) - 65, left: 0, behavior: "auto" });
    }

    const resetResults = () => {
        filter(props.getDefaultFilterState(userSettings));
    }

    const onShowMorePress = () => {
        const stateCopy: ItemDisplayState<TFilterState> = JSON.parse(JSON.stringify(displayState));
        stateCopy.itemsDisplayed = stateCopy.itemsDisplayed + batchSize;
        store.dispatch(setDisplayState(stateCopy));
    }

    const setIsFilterModalOpen = (newIsFilterModalOpen: boolean) => {
        const stateCopy: ItemDisplayState<TFilterState> = JSON.parse(JSON.stringify(displayState));
        stateCopy.isFilterModalOpen = newIsFilterModalOpen;
        store.dispatch(setDisplayState(stateCopy));
    }

    const update = (updateFunc: (copy: ItemDisplayState<TFilterState>) => void) => {
        const stateCopy: ItemDisplayState<TFilterState> = JSON.parse(JSON.stringify(displayState));
        updateFunc(stateCopy);
        store.dispatch(setDisplayState(stateCopy));
    }

    useEffect(() => {
        if (window.location.search || props.enableScrollRefresh) {
            needsDisplayStateRestore.current = true;
            return;
        }
    }, []);

    useEffect(() => {
        if (!displayState) {
            return;
        }

        // Don't keep track of scrollY if it's not enabled.
        if (!props.enableScrollRefresh) {
            return;
        }

        // If state hasn't been refreshed, pause recording changes to scroll.
        if (needsDisplayStateRestore.current) {
            return;
        }

        const stateCopy: ItemDisplayState<TFilterState> = JSON.parse(JSON.stringify(displayState));
        stateCopy.scrollY = scrollY;
        store.dispatch(setDisplayState(stateCopy));
    }, [scrollY, props.enableScrollRefresh]);

    useEffect(() => {
        const newDisplayState: ItemDisplayState<TFilterState> = displayState
            ? JSON.parse(JSON.stringify(displayState))
            : {
                id: props.displayStateId,
                scrollY: 0,
                itemsDisplayed: batchSize,
                filters: props.getDefaultFilterState(userSettings),
                isFilterModalOpen: false
            };

        if (needsDisplayStateRestore.current && props.queryParamParser && props.items.length > 0) {
            const urlParams = new URLSearchParams(window.location.search);
            newDisplayState.filters = props.queryParamParser.getFilterState(urlParams, () => props.getDefaultFilterState(userSettings));
        }

        const matchingItems = props.filter(props.items, newDisplayState.filters);

        if (props.onFilter) {
            props.onFilter(matchingItems);
        }

        setItems(matchingItems);
        setNumberOfFiltersApplied(calculateNumberOfFiltersApplied(newDisplayState.filters, props.getDefaultFilterState(userSettings)));

        if (props.enableScrollRefresh && needsDisplayStateRestore.current) {
            newDisplayState.itemsDisplayed = displayState?.itemsDisplayed ?? batchSize;
            newDisplayState.scrollY = displayState?.scrollY ?? 0;

            setTimeout(() => {
                window.scrollTo({ top: newDisplayState.scrollY, left: 0, behavior: "smooth" });
                needsDisplayStateRestore.current = false;
            }, 250);
        }

        store.dispatch(setDisplayState(newDisplayState));
    }, [props.items, props.refreshTrigger, props.getDefaultFilterState]);

    if (props.isLoading || !displayState) {
        return <Spinner />
    }

    return (
        <>
            <ItemFilterModal
                modalBody={props.modalBody}
                modalBodyOptionalProps={props.modalBodyOptionalProps}
                currentFilterState={displayState.filters}
                getDefaultFilterState={() => props.getDefaultFilterState(userSettings)}
                isOpen={displayState.isFilterModalOpen}
                onOpenChange={(newIsOpen) => setIsFilterModalOpen(newIsOpen)}
                onApplyFilters={(newFilterState: TFilterState) => filter(newFilterState)} />

            <ItemDisplayToolbar
                numberOfFiltersApplied={numberOfFiltersApplied}
                tooltipPosition={props.toolbarPosition || "top"}
                hideFilterButton={props.hideFilterButton}
                hideResetButton={props.hideResetButton}
                searchText={displayState.filters.searchText || ""}
                onFiltersClick={() => setIsFilterModalOpen(true)}
                classNames={{ container: props.classNames?.toolbar?.container }}
                onResetClick={() => resetResults()}
                onSearchTextChange={(searchText) => {
                    update((copy) => copy.filters.searchText = searchText);
                }}
                onSearchTextEnterKey={(newSearchText) => {
                    const filterStateCopy = JSON.parse(JSON.stringify(displayState.filters)) as TFilterState;
                    filterStateCopy.searchText = newSearchText;
                    filter(filterStateCopy);
                }} />

            <div ref={contentStartRef}></div>

            <ItemDisplay
                classNames={{ itemsContainer: props.classNames?.itemsContainer }}
                items={items}
                itemsDisplayed={displayState.itemsDisplayed}
                onShowMoreClick={() => onShowMorePress()}
                itemComponentGenerator={(item, index) => props.itemComponentGenerator(item, index)}
            />
        </>
    )
}